// Server events: admin-scheduled windows that raise kill XP, drops or kill
// coins for everyone, free (never sold). Aura odds and diamonds are never an
// event kind. An event multiplies on top of every other boost (shop boosts,
// VIP, pass, Guild Boost, Guild War bonus, traits, relics); events of the
// same kind running at once multiply together, capped at EVENTS.maxMult.
// Times are UTC ms. A region limits it to that region's hunts.
// Evidence: the reference's global event stacks XP/farm buffs on top of every
// boost (pokeidle 03-hunts-xp-progression.md:254, section 9).
import { REGIONS } from './grounds.ts';

export type EventKind = 'xp' | 'drop' | 'coin';

export interface ServerEvent {
  id: number;
  kind: EventKind;
  mult: number;
  start: number;
  end: number;
  /** A region id; absent = every hunt. */
  region?: string;
}

export const EVENT_KINDS: Record<EventKind, { name: string; what: string }> = {
  xp: { name: 'XP', what: 'kill XP' },
  drop: { name: 'Drops', what: 'drop chance (items and relics)' },
  coin: { name: 'Coins', what: 'kill coins' },
};

export const EVENTS = {
  minMult: 1.25,
  maxMult: 3,
  minMinutes: 15,
  maxDays: 7,
  /** Open or upcoming events at once. */
  maxScheduled: 20,
  /** How far ahead an event may start. */
  aheadDays: 90,
} as const;

export function isEventKind(k: unknown): k is EventKind { return k === 'xp' || k === 'drop' || k === 'coin'; }

/** The stored start: one in the past is moved up to (just before) now. */
export function eventStart(start: number, now: number): number { return Math.max(Math.floor(start), now - 60_000); }

/** Why an event can't be scheduled, or ''. Length is checked from the stored start. */
export function eventProblem(e: { kind: unknown; mult: unknown; start: unknown; end: unknown; region?: unknown }, now: number): string {
  if (!isEventKind(e.kind)) return 'Kind must be XP, drops or coins.';
  const mult = Number(e.mult), end = Number(e.end);
  let start = Number(e.start);
  if (!Number.isFinite(mult) || mult < EVENTS.minMult || mult > EVENTS.maxMult) return `Multiplier must be ${EVENTS.minMult} to ${EVENTS.maxMult}.`;
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 'Start and end must be dates.';
  start = eventStart(start, now);
  if (end <= now) return 'The end is in the past.';
  if (end - start < EVENTS.minMinutes * 60_000) return `An event lasts at least ${EVENTS.minMinutes} minutes.`;
  if (end - start > EVENTS.maxDays * 86_400_000) return `An event lasts at most ${EVENTS.maxDays} days.`;
  if (start > now + EVENTS.aheadDays * 86_400_000) return `Start within ${EVENTS.aheadDays} days.`;
  if (e.region !== undefined && e.region !== '' && !REGIONS.some((r) => r.id === e.region)) return 'Unknown region.';
  return '';
}

export function eventActive(e: ServerEvent, now: number) { return e.start <= now && now < e.end; }

/** The multiplier events give this kind on a hunt in `region` now (1 when none). */
export function eventMult(list: readonly ServerEvent[], kind: EventKind, region: string, now: number): number {
  let m = 1;
  for (const e of list) if (e.kind === kind && eventActive(e, now) && (!e.region || e.region === region)) m *= e.mult;
  return Math.min(EVENTS.maxMult, m);
}

/** "XP ×2", "Drops ×1.5 · Hellas" */
export function eventLabel(e: ServerEvent): string {
  const r = e.region ? REGIONS.find((x) => x.id === e.region)?.name ?? e.region : '';
  return `${EVENT_KINDS[e.kind].name} ×${+e.mult.toFixed(2)}${r ? ` · ${r}` : ''}`;
}
